// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  Alert,
  Anchor,
  Avatar,
  Badge,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import type { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconArrowLeft } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '../../constants/appointmentTypes';
import { getExtensionValue, TIMEZONE_EXTENSION } from '../../utils/practitioner';
import {
  EMPTY_VALUE,
  formatLicenseStates,
  getBirthDate,
  getCertification,
  getCredentialSummaries,
  getDea,
  getDegree,
  getEmail,
  getLicenseStates,
  getNpi,
  getPhone,
  getPhoto,
  getPractitionerName,
  getPtan,
  getRoleCode,
  getRoleDisplay,
  getStatus,
  getTaxonomy,
  pickPrimaryRole,
} from './staffing-utils';

interface DetailField {
  label: string;
  value: string;
}

function FieldGrid({ fields }: { fields: DetailField[] }): JSX.Element {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
      {fields.map((field) => (
        <Card key={field.label} withBorder padding="sm" radius="sm">
          <Text size="xs" c="dimmed" fw={500} tt="uppercase">
            {field.label}
          </Text>
          <Text size="sm" mt={4} style={{ wordBreak: 'break-word' }}>
            {field.value}
          </Text>
        </Card>
      ))}
    </SimpleGrid>
  );
}

export function StaffDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState<Practitioner | undefined>();
  const [practitionerRole, setPractitionerRole] = useState<PractitionerRole | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let active = true;

    async function load(practitionerId: string): Promise<void> {
      const p = await medplum.readResource('Practitioner', practitionerId);
      if (!active) {
        return;
      }
      setPractitioner(p);
      const practitionerRef = getReferenceString(p);
      const roles = await medplum.searchResources('PractitionerRole', {
        practitioner: practitionerRef,
        _count: '20',
      });
      if (!active) {
        return;
      }
      const primary = pickPrimaryRole(roles);
      console.debug('[StaffDetailPage] loaded', {
        practitionerId: p.id,
        practitionerRef,
        practitionerActive: p.active,
        roleCount: roles.length,
        roles: roles.map((r) => ({
          id: r.id,
          active: r.active,
          coding: r.code?.[0]?.coding,
        })),
        primaryRoleId: primary?.id,
        primaryRoleCode: getRoleCode(primary),
      });
      if (roles.length > 0 && getRoleCode(primary) === undefined) {
        console.warn('[StaffDetailPage] PractitionerRole(s) present but no Polaris role coding found.', {
          practitionerRef,
          expectedSystem: POLARIS_PROVIDER_ROLE_SYSTEM,
          candidateCodings: roles.map((r) => r.code?.[0]?.coding),
        });
      }
      if (roles.length === 0) {
        console.warn('[StaffDetailPage] No PractitionerRole found for', practitionerRef);
      }
      setPractitionerRole(primary);
    }

    load(id)
      .catch((err) => {
        console.error('[StaffDetailPage] Failed to load practitioner:', err);
        if (active) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, id]);

  if (loading) {
    return (
      <Paper p="xl" mx="auto" mt="xl">
        <Group justify="center">
          <Loader />
        </Group>
      </Paper>
    );
  }

  if (error || !practitioner) {
    return (
      <Stack gap="md" m="md">
        <Anchor component="button" type="button" onClick={() => navigate(-1)} data-testid="staff-detail-back">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            <Text size="sm">Back</Text>
          </Group>
        </Anchor>
        <Alert color="red" title="Could not load staff member">
          {error ?? 'Practitioner not found.'}
        </Alert>
      </Stack>
    );
  }

  const status = getStatus(practitioner, practitionerRole);
  const photo = getPhoto(practitioner);
  const timezone = getExtensionValue(practitioner, TIMEZONE_EXTENSION) || EMPTY_VALUE;
  const profileFields: DetailField[] = [
    { label: 'Account Type', value: getRoleDisplay(practitionerRole) },
    { label: 'Email', value: getEmail(practitioner) },
    { label: 'Phone', value: getPhone(practitioner) },
    { label: 'Date of Birth', value: getBirthDate(practitioner) },
    { label: 'Timezone', value: timezone },
  ];
  const credentialingFields: DetailField[] = [
    { label: 'NPI', value: getNpi(practitioner) },
    { label: 'DEA', value: getDea(practitioner) },
    { label: 'PTAN', value: getPtan(practitioner) },
    { label: 'Degree', value: getDegree(practitioner) },
    { label: 'Certification', value: getCertification(practitioner) },
    { label: 'Taxonomy', value: getTaxonomy(practitioner) },
  ];
  const licenseStates = getLicenseStates(practitioner);
  const credentialRows = getCredentialSummaries(practitioner);

  return (
    <Stack gap="md" m="md">
      <Anchor component="button" type="button" onClick={() => navigate(-1)} data-testid="staff-detail-back">
        <Group gap={4}>
          <IconArrowLeft size={14} />
          <Text size="sm">Back to Staffing</Text>
        </Group>
      </Anchor>

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group align="center" wrap="nowrap" gap="md">
            <Avatar src={photo?.url} alt={getPractitionerName(practitioner)} size="lg" radius="xl" />
            <Stack gap={2}>
              <Title order={2}>{getPractitionerName(practitioner)}</Title>
              <Text size="sm" c="dimmed">
                {getRoleDisplay(practitionerRole)}
              </Text>
            </Stack>
          </Group>
          <Badge color={status === 'Active' ? 'green' : 'gray'} variant="light" size="lg">
            {status}
          </Badge>
        </Group>
      </Paper>

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Stack gap="md">
          <Title order={3}>Provider Information</Title>
          <FieldGrid fields={profileFields} />
        </Stack>
      </Paper>

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Stack gap="md">
          <Title order={3}>Credentialing</Title>
          <FieldGrid fields={credentialingFields} />
        </Stack>
      </Paper>

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Stack gap="md">
          <Title order={3}>Licenses</Title>
          {licenseStates.length === 0 ? (
            <Text size="sm" c="dimmed">
              No licenses on file.
            </Text>
          ) : (
            <Group gap="xs">
              {licenseStates.map((state) => (
                <Badge key={state} variant="light" color="blue" size="lg">
                  {state}
                </Badge>
              ))}
            </Group>
          )}
          <Text size="xs" c="dimmed">
            Summary: {formatLicenseStates(licenseStates)}
          </Text>
        </Stack>
      </Paper>

      <Paper withBorder p="lg" radius="md" shadow="sm">
        <Stack gap="md">
          <Title order={3}>Credentials</Title>
          {credentialRows.length === 0 ? (
            <Text size="sm" c="dimmed">
              No credentials on file.
            </Text>
          ) : (
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Payor</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Plan ID</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {credentialRows.map((row) => (
                  <Table.Tr key={`${row.payor}|${row.planId}|${row.status}`}>
                    <Table.Td>{row.payor}</Table.Td>
                    <Table.Td>{row.status}</Table.Td>
                    <Table.Td>{row.planId}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
